import dayjs from 'dayjs';
import DATE_FORMATS from '@/constants/dateFormats';

const defaultIntlOptions = {
    dateStyle: 'short',
    timeStyle: 'medium'
};

/**
 * Formats a given date according to specified options.
 * Provides flexible formatting options, including Day.js formats, revert order, and locale-based formatting.
 *
 * @param {Date|string|number} dateInput - The date to format, can be a Date object, timestamp, or date string.
 * @param {Object} [options] - Configuration options for formatting the date.
 * @param {string} [options.format=DATE_FORMATS.FULL_DATETIME] - The Day.js format string, used when `useIntl` is false.
 * @param {boolean} [options.revert=false] - Whether to format the date in day-month-year order with custom separator.
 * @param {string} [options.separator='-'] - Separator for the reverted date format, used only if `revert` is true.
 * @param {string} [options.locale='pl'] - Locale for Intl.DateTimeFormat, e.g., 'en-US' or 'pl'.
 * @param {Object} [options.intlOptions={ dateStyle: 'short', timeStyle: 'medium' }] - Options for Intl.DateTimeFormat.
 * @param {boolean} [options.useIntl=false] - Whether to use Intl.DateTimeFormat for locale-specific formatting.
 *
 * @throws {Error} Throws an error if the provided date is invalid.
 *
 * @returns {string} - The formatted date string based on specified options.
 *
 * @example
 * // Basic Day.js formatting
 * formatDate(new Date(), { format: 'YYYY-MM-DD' });
 *
 * @example
 * // Reverted format with custom separator
 * formatDate(new Date(), { revert: true, separator: '/' });
 *
 * @example
 * // Locale-specific formatting with Intl.DateTimeFormat
 * formatDate(new Date(), { useIntl: true, locale: 'en-US' });
 */
function formatDate (dateInput, {
    format = DATE_FORMATS.FULL_DATETIME,
    revert = false,
    separator = '-',
    locale = 'pl',
    intlOptions = defaultIntlOptions,
    useIntl = false,
    useISO = false
} = {}) {
    const date = new Date(dateInput);
    if (isNaN(date)) throw new Error('Invalid date input');

    // Revert format (e.g., DD-MM-YYYY)
    if (revert) {
        const day = `${date.getDate()}`.padStart(2, '0');
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const year = date.getFullYear();
        return [day, month, year].join(separator);
    }

    // Intl.DateTimeFormat for locale-specific formats
    if (useIntl) {
        return new Intl.DateTimeFormat(locale, intlOptions).format(date);
    }

    if (useISO) {
        return date.toISOString();
    }

    // Day.js for custom formats
    return dayjs(date).format(format);
}

export {
    formatDate,
    defaultIntlOptions,
    DATE_FORMATS
};
